// smart-form.ts

import { FormGroup, AbstractControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subscription, finalize } from 'rxjs';

export interface SmartFormSubmitOptions {
  showSuccessSnackbar?: boolean;
  successMessage?: string | ((response: unknown) => string | null | undefined);
  invalidMessage?: string;
  onSettled?: () => void;
}

export interface SmartFormAutosaveOptions {
  canSave?: () => boolean;
  snapshot?: () => unknown;
  loopWindowMs?: number;
  maxSavesPerWindow?: number;
  loopMessage?: string;
}

interface SmartFormSubmitRuntimeOptions {
  markSubmitted?: boolean;
  showInvalidSnackbar?: boolean;
  autosaveSignature?: string;
}

export class SmartForm<T = any> {

  loading = false;
  submitted = false;

  // ✅ NEW: Step support
  private steps: string[] = [];
  private currentStepIndex = 0;
  private labels: Record<string, string> = {};
  private autosaveSubscription?: Subscription;
  private autosaveTimer?: ReturnType<typeof setTimeout>;
  private lastAutosaveSignature: string | null = null;
  private pendingAutosaveSignature: string | null = null;
  private autosaveAttempts: Array<{ signature: string; timestamp: number }> = [];

  constructor(
    public form: FormGroup,
    private snackBar: MatSnackBar,
    private submitHandler: (value: T) => Observable<any>,
    private onSuccess?: (res: any) => void,
    private onError?: (err: any) => void,
    private submitOptions: SmartFormSubmitOptions = {}
  ) { }

  get value(): T {
    return this.form.value;
  }

  get invalid() {
    return this.form.invalid;
  }

  get pending() {
    return this.loading || this.form.pending;
  }

  control(path: string): AbstractControl | null {
    const directControl = this.form.controls[path];
    return directControl ?? this.form.get(path);
  }

  /** Keep hidden or locked inputs out of validation without discarding their values. */
  setControlsActive(paths: string[], active: boolean): void {
    for (const path of paths) {
      const control = this.control(path);
      if (!control || control.enabled === active) continue;
      if (active) control.enable({ emitEvent: false });
      else control.disable({ emitEvent: false });
    }
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  subscribeValueChanges(path: string, callback: (value: any) => void) {
    const control = this.control(path);
    const sub = control?.valueChanges.subscribe(callback);
    return () => sub?.unsubscribe();
  }

  getError(path: string): string | null {
    const control = this.control(path);

    if (!control || !control.errors) return null;
    if (!(control.touched || control.dirty || this.submitted)) return null;

    const error = Object.values(control.errors)[0] as any;
    return error?.message || 'Invalid field';
  }

  isControlInvalid(path: string): boolean {
    const control = this.control(path);
    if (!control) return false;
    return control.invalid && (control.touched || control.dirty || this.submitted);
  }

  setSteps(steps: string[]) {
    this.steps = steps;
    this.currentStepIndex = 0;
  }

  get currentStep(): string | null {
    return this.steps.length ? this.steps[this.currentStepIndex] : null;
  }

  goToStep(step: string): boolean {
    const nextIndex = this.steps.indexOf(step);
    if (nextIndex < 0 || nextIndex > this.currentStepIndex) return false;
    this.currentStepIndex = nextIndex;
    return true;
  }

  isLastStep(): boolean {
    return this.currentStepIndex === this.steps.length - 1;
  }

  nextStep(): boolean {

    if (!this.currentStep) return true;

    const group = this.form.get(this.currentStep);

    if (group?.invalid || group?.pending) {
      group?.markAllAsTouched();
      return false;
    }

    this.currentStepIndex++;
    return true;
  }

  prevStep() {
    if (this.currentStepIndex > 0) {
      this.currentStepIndex--;
    }
  }

  isCurrentStepValid(): boolean {
    if (!this.currentStep) return this.form.valid;
    const group = this.form.get(this.currentStep);
    return !!group && group.valid && !group.pending;
  }

  setLabels(labels: Record<string, string>) {
    this.labels = labels;
  }

  private collectErrors(
    control: AbstractControl | null,
    path: string = ''
  ): { field: string; message: string }[] {

    if (!control) return [];

    let errors: { field: string; message: string }[] = [];

    const fieldKey = path.split('.').pop() || '';

    const label =
      this.labels[path] ||
      this.labels[fieldKey] ||
      fieldKey.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());

    // ✅ REMOVE touched requirement
    if (control.errors) {

      const firstError = Object.values(control.errors)[0] as any;

      errors.push({
        field: label,
        message: firstError?.message || 'Invalid field'
      });

    }

    // recurse into children
    if ((control as any).controls) {

      const group = control as any;

      Object.keys(group.controls).forEach(key => {
        const child = group.controls[key];
        const childPath = path ? `${path}.${key}` : key;

        errors = errors.concat(this.collectErrors(child, childPath));
      });

    }

    return errors;
  }

  getCurrentStepErrorsDetailed(): { field: string; message: string }[] {

    if (!this.currentStep) return [];

    const group = this.form.get(this.currentStep);

    if (!group) return [];

    return this.collectErrors(group, this.currentStep);
  }

  getCurrentStepErrorMessage(): string {

    const errors = this.getCurrentStepErrorsDetailed();

    if (!errors.length) return '';

    const unique = new Map<string, string>();

    errors.forEach(e => {
      unique.set(e.field, e.message);
    });

    return Array.from(unique.entries())
      .map(([field, msg]) => `${field}: ${msg}`)
      .join(', ');
  }

  submit(options: SmartFormSubmitRuntimeOptions = {}) {
    if (this.loading) {
      return;
    }

    const markSubmitted = options.markSubmitted !== false;
    const showInvalidSnackbar = options.showInvalidSnackbar !== false;

    if (markSubmitted) {
      this.submitted = true;
    }

    if (this.form.invalid || this.form.pending) {
      if (markSubmitted) {
        this.form.markAllAsTouched();
      }

      if (showInvalidSnackbar) {
        this.snackBar.open(
          this.submitOptions.invalidMessage || this.resolveInvalidMessage(),
          'Close',
          {
            duration: 6000,
            panelClass: ['error-snackbar'],
            verticalPosition: 'top',
            horizontalPosition: 'center',
          }
        );
      }

      return;
    }

    this.loading = true;

    this.submitHandler(this.value)
      .pipe(finalize(() => this.finishLoadingAfterCurrentCheck()))
      .subscribe({
        next: res => {
          if (options.autosaveSignature) {
            this.lastAutosaveSignature = options.autosaveSignature;
          }
          this.pendingAutosaveSignature = null;

          if (this.submitOptions.showSuccessSnackbar !== false && !options.autosaveSignature) {
            const successMessage = this.resolveSuccessMessage(res);
            if (successMessage) {
              this.snackBar.open(
                successMessage,
                'Close',
                {
                  duration: 3000,
                  verticalPosition: 'top',
                  horizontalPosition: 'center',
                  panelClass: ['success-snackbar'],
                }
              );
            }
          }
          this.onSuccess?.(res);
        },
        error: err => {
          if (options.autosaveSignature) {
            this.pendingAutosaveSignature = null;
          }
          this.handleServerError(err);
          this.onError?.(err);
        }
      });

  }

  private resolveSuccessMessage(response: unknown): string | null {
    const configuredMessage = this.submitOptions.successMessage;
    if (typeof configuredMessage === 'function') {
      return configuredMessage(response)?.trim() || null;
    }

    return configuredMessage?.trim() || 'Saved successfully.';
  }

  autosave(
    waitMs: number = 1000,
    canSaveOrOptions?: (() => boolean) | SmartFormAutosaveOptions
  ): () => void {
    this.stopAutosave();

    const options = this.resolveAutosaveOptions(canSaveOrOptions);
    this.lastAutosaveSignature = this.createAutosaveSignature(options.snapshot);
    this.pendingAutosaveSignature = null;
    this.autosaveAttempts = [];

    this.autosaveSubscription = this.form.valueChanges.subscribe(() => {
      if (this.autosaveTimer) {
        clearTimeout(this.autosaveTimer);
      }

      this.autosaveTimer = setTimeout(() => {
        if (options.canSave && !options.canSave()) {
          return;
        }

        if (this.loading || this.form.invalid || this.form.pending) {
          return;
        }

        const signature = this.createAutosaveSignature(options.snapshot);
        if (
          signature === this.lastAutosaveSignature ||
          signature === this.pendingAutosaveSignature
        ) {
          return;
        }

        if (this.detectAutosaveLoop(signature, options)) {
          return;
        }

        this.pendingAutosaveSignature = signature;
        this.submit({
          markSubmitted: false,
          showInvalidSnackbar: false,
          autosaveSignature: signature,
        });
      }, waitMs);
    });

    return () => this.stopAutosave();
  }

  stopAutosave(): void {
    this.autosaveSubscription?.unsubscribe();
    this.autosaveSubscription = undefined;

    if (this.autosaveTimer) {
      clearTimeout(this.autosaveTimer);
      this.autosaveTimer = undefined;
    }
  }

  resetAutosaveMemory(): void {
    this.lastAutosaveSignature = null;
    this.pendingAutosaveSignature = null;
    this.autosaveAttempts = [];
  }

  private finishLoadingAfterCurrentCheck(): void {
    setTimeout(() => {
      this.loading = false;
      this.submitOptions.onSettled?.();
    }, 0);
  }

  private resolveAutosaveOptions(
    canSaveOrOptions?: (() => boolean) | SmartFormAutosaveOptions
  ): Required<Pick<SmartFormAutosaveOptions, 'loopWindowMs' | 'maxSavesPerWindow' | 'loopMessage'>> &
    Pick<SmartFormAutosaveOptions, 'canSave' | 'snapshot'> {
    if (typeof canSaveOrOptions === 'function') {
      return {
        canSave: canSaveOrOptions,
        snapshot: undefined,
        loopWindowMs: 15000,
        maxSavesPerWindow: 8,
        loopMessage: 'Autosave loop detected. Autosave was stopped for this form.',
      };
    }

    return {
      canSave: canSaveOrOptions?.canSave,
      snapshot: canSaveOrOptions?.snapshot,
      loopWindowMs: canSaveOrOptions?.loopWindowMs ?? 15000,
      maxSavesPerWindow: canSaveOrOptions?.maxSavesPerWindow ?? 8,
      loopMessage:
        canSaveOrOptions?.loopMessage ??
        'Autosave loop detected. Autosave was stopped for this form.',
    };
  }

  private createAutosaveSignature(snapshot?: () => unknown): string {
    const value = snapshot ? snapshot() : this.value;
    return this.stableStringify(this.normalizeAutosaveValue(value));
  }

  private normalizeAutosaveValue(value: unknown): unknown {
    if (value === undefined) {
      return null;
    }

    if (value === null || typeof value !== 'object') {
      return typeof value === 'bigint' ? value.toString() : value;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value.toISOString();
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.normalizeAutosaveValue(item));
    }

    const normalized: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      normalized[key] = this.normalizeAutosaveValue((value as Record<string, unknown>)[key]);
    }

    return normalized;
  }

  private stableStringify(value: unknown): string {
    return JSON.stringify(value);
  }

  private detectAutosaveLoop(
    signature: string,
    options: Required<Pick<SmartFormAutosaveOptions, 'loopWindowMs' | 'maxSavesPerWindow' | 'loopMessage'>>
  ): boolean {
    const now = Date.now();
    const windowStart = now - options.loopWindowMs;
    this.autosaveAttempts = this.autosaveAttempts.filter(
      (attempt) => attempt.timestamp >= windowStart
    );
    this.autosaveAttempts.push({ signature, timestamp: now });

    if (this.autosaveAttempts.length <= options.maxSavesPerWindow) {
      return false;
    }

    this.stopAutosave();
    this.pendingAutosaveSignature = null;
    const error = new Error(options.loopMessage);

    this.snackBar.open(options.loopMessage, 'Close', {
      duration: 8000,
      panelClass: ['error-snackbar'],
      verticalPosition: 'top',
      horizontalPosition: 'center',
    });

    setTimeout(() => {
      throw error;
    }, 0);

    return true;
  }

  private resolveInvalidMessage(): string {
    const details = this.currentStep
      ? this.getCurrentStepErrorMessage()
      : this.collectErrors(this.form)
        .slice(0, 3)
        .map((entry) => `${entry.field}: ${entry.message}`)
        .join(', ');

    if (!details) {
      return 'Please fix the errors in the form.';
    }

    return `Please fix the errors in the form. ${details}`;
  }

  private handleServerError(err: any) {

    const message = err?.error?.message || 'Request failed';

    this.snackBar.open(
      message,
      'Close',
      {
        duration: 8000,
        verticalPosition: 'top',
        horizontalPosition: 'center',
        panelClass: ['error-snackbar'],
      }
    );

    if (err?.error?.fields) {

      Object.entries(err.error.fields).forEach(([field, msg]) => {

        const control = this.form.get(field);

        if (control) {
          control.setErrors({
            server: { message: msg }
          });
        }

      });

    }

  }

}
